/**
 * VulnerabilityRecordRow Component
 *
 * Record row for vulnerability detector card.
 * Matches RecordsTable styling with conduct score.
 *
 * Atomic level: Molecule
 * Used by: ActionCardsSection (Vulnerability Detector card)
 */

import React from 'react';
import { ChevronRight } from 'lucide-react';
import { ConductScoreCircle } from '../../../ui/conduct-score-circle';
import { Badge } from '../../../ui/badge';
import type { VulnerabilityRecordRowProps } from '../types/today.types';

/**
 * Format time (e.g., "3:45 PM")
 */
const formatTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-GB', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

/**
 * Record row for vulnerability detector card, matching RecordsTable styling.
 */
export function VulnerabilityRecordRow({
  record,
  onClick,
}: VulnerabilityRecordRowProps): React.ReactElement {
  return (
    <button
      type="button"
      className="w-full px-5 py-4 text-left transition-colors hover:bg-accent/50 focus-visible:bg-accent/50 focus-visible:outline-none"
      onClick={() => onClick(record.id, record.collectionId)}
    >
      {/* Main row */}
      <div className="flex items-center gap-3">
        {/* Conduct Score */}
        {record.conductScore !== null && (
          <ConductScoreCircle score={record.conductScore} size="sm" className="shrink-0" />
        )}

        {/* Time */}
        <span className="w-20 flex-shrink-0 text-[12px] text-muted-foreground">
          {formatTime(record.receivedAt)}
        </span>

        {/* Tags */}
        <div className="flex shrink-0 items-center gap-1">
          {record.labels.slice(0, 2).map((label) => (
            <Badge
              key={label}
              variant="secondary"
              className="rounded-full px-2 py-0 text-[10px] font-medium text-muted-foreground"
            >
              {label}
            </Badge>
          ))}
          {record.labels.length > 2 && (
            <span className="text-[10px] text-muted-foreground/70">
              +{record.labels.length - 2}
            </span>
          )}
        </div>

        {/* Customer Name */}
        <span className="w-32 flex-shrink-0 truncate text-[13px] font-medium text-foreground">
          {record.customerName || '—'}
        </span>

        {/* Summary */}
        <p className="flex-1 truncate text-[13px] text-muted-foreground">{record.summary}</p>

        {/* Chevron */}
        <ChevronRight className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
      </div>
    </button>
  );
}
